// Top-left menu bar: brand, dataset/field pickers, action icons.
//
// The bar is non-draggable chrome (intentionally not a `.pane`), pinned at
// top-left and always visible. It mirrors params.dataset / params.fieldComponent
// in two custom dropdowns and shares the upload flow with the Data
// folder so both stay in sync regardless of which the user touches.

import { store, params } from '@store/instance';
import { INTENTS } from '@store/intents';
import { datasetOptions } from '../panel/options';
import { buildFieldComponentOptionsFromMetadata } from '@app/fieldOptions';
import { pickAndUploadHDF5, UPLOADED_DATASETS_CHANGED } from '@app/uploadHDF5';
import { createDropdown, type DropdownHandle, type DropdownOption } from './dropdown';
import { installTimestepScrubber } from './timestepScrubber';
import { installTopbarFit } from './fit';
import { installTopbarReveal } from './reveal';
import type { ViewerContext } from '@app/context';

export function initTopbar(viewer: ViewerContext): () => void {
    const root = document.getElementById('topbar-menu');
    if (!root) return () => { /* nothing wired */ };

    const datasetBtn   = root.querySelector<HTMLButtonElement>('#topbar-dataset');
    const datasetLabel = root.querySelector<HTMLElement>('#topbar-dataset-label');
    const fieldBtn     = root.querySelector<HTMLButtonElement>('#topbar-field');
    const fieldLabel   = root.querySelector<HTMLElement>('#topbar-field-label');
    const uploadBtn    = root.querySelector<HTMLButtonElement>('#topbar-upload');
    const helpBtn      = root.querySelector<HTMLButtonElement>('#topbar-help');
    const consoleBtn   = root.querySelector<HTMLButtonElement>('#topbar-console');
    const expandBtn    = root.querySelector<HTMLButtonElement>('#topbar-expand');
    const disabledBtns = root.querySelectorAll<HTMLButtonElement>('.topbar-icon.is-disabled');

    // ---- label sources ----
    const labelForDataset = (key: string | null | undefined): string => {
        if (!key) return '—';
        const opts = datasetOptions(viewer);
        const match = Object.entries(opts).find(([, value]) => value === key);
        return match ? match[0] : key;
    };
    const labelForField = (value: string | null | undefined): string => {
        if (!value) return '—';
        const opts = buildFieldComponentOptionsFromMetadata(viewer.field.metadata);
        const match = Object.entries(opts).find(([, v]) => v === value);
        return match ? match[0] : value;
    };

    const refreshDatasetLabel = (): void => {
        if (!datasetLabel) return;
        const key = params.dataset || viewer.field.currentDataset || '';
        datasetLabel.textContent = labelForDataset(key);
    };
    const refreshFieldLabel = (): void => {
        if (!fieldLabel) return;
        const value = params.fieldComponent || viewer.field.currentComponent || '';
        fieldLabel.textContent = labelForField(value);
    };

    // ---- dropdowns ----
    let datasetDD: DropdownHandle | null = null;
    let fieldDD:   DropdownHandle | null = null;

    if (datasetBtn) {
        datasetDD = createDropdown({
            trigger: datasetBtn,
            getOptions: (): DropdownOption[] => {
                const map = datasetOptions(viewer);
                return Object.entries(map).map(([label, value]) => ({ label, value }));
            },
            getSelected: () => params.dataset || null,
            onSelect: (value) => {
                // Pure dispatcher — the SWITCH_DATASET handler owns params.dataset.
                store.dispatch(INTENTS.SWITCH_DATASET, { key: value });
                refreshDatasetLabel();
            },
        });
    }

    if (fieldBtn) {
        fieldDD = createDropdown({
            trigger: fieldBtn,
            getOptions: (): DropdownOption[] => {
                const map = buildFieldComponentOptionsFromMetadata(viewer.field.metadata);
                return Object.entries(map).map(([label, value]) => ({ label, value }));
            },
            getSelected: () => params.fieldComponent || null,
            onSelect: (value) => {
                // Pure dispatcher — the SET_FIELD_COMPONENT handler owns the param.
                store.dispatch(INTENTS.SET_FIELD_COMPONENT, { component: value });
                refreshFieldLabel();
            },
        });
    }

    // ---- icon buttons ----
    const onUploadClick = (): void => pickAndUploadHDF5();
    if (uploadBtn) uploadBtn.addEventListener('click', onUploadClick);

    const helpPanel = document.getElementById('help-panel');
    const refreshHelpPressed = (): void => {
        if (!helpBtn || !helpPanel) return;
        helpBtn.setAttribute('aria-pressed', helpPanel.classList.contains('show') ? 'true' : 'false');
    };
    const onHelpClick = (): void => {
        if (!helpPanel) return;
        helpPanel.classList.toggle('show');
        refreshHelpPressed();
    };
    if (helpBtn) helpBtn.addEventListener('click', onHelpClick);

    // Watch the help panel from outside (#help-close, keyboard, helpStart)
    // so the icon's pressed state stays accurate.
    let helpObserver: MutationObserver | null = null;
    if (helpPanel) {
        helpObserver = new MutationObserver(refreshHelpPressed);
        helpObserver.observe(helpPanel, { attributes: true, attributeFilter: ['class'] });
        refreshHelpPressed();
    }

    // Console button: same shape as help — toggle .show, mirror to
    // aria-pressed, observe external changes (close button, etc.).
    const consolePanel = document.getElementById('console-panel');
    const refreshConsolePressed = (): void => {
        if (!consoleBtn || !consolePanel) return;
        consoleBtn.setAttribute('aria-pressed', consolePanel.classList.contains('show') ? 'true' : 'false');
    };
    const onConsoleClick = (): void => {
        if (!consolePanel) return;
        consolePanel.classList.toggle('show');
        refreshConsolePressed();
    };
    if (consoleBtn) consoleBtn.addEventListener('click', onConsoleClick);
    let consoleObserver: MutationObserver | null = null;
    if (consolePanel) {
        consoleObserver = new MutationObserver(refreshConsolePressed);
        consoleObserver.observe(consolePanel, { attributes: true, attributeFilter: ['class'] });
        refreshConsolePressed();
    }

    // Expand toggle: clicking pins the actions popup (and the compact-mode
    // timestep) open. Hovering/focusing the chevron expands it transiently; this
    // pin is the working opener on touch, where there is no hover signal.
    const onExpandClick = (): void => {
        const open = root.classList.toggle('is-expanded');
        expandBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');
        // On close, drop focus so `:focus-within` doesn't keep the tray open.
        if (!open) expandBtn?.blur();
    };
    if (expandBtn) expandBtn.addEventListener('click', onExpandClick);

    // Disabled placeholders: swallow clicks (cursor:not-allowed already in CSS).
    const onDisabledClick = (e: MouseEvent): void => { e.preventDefault(); e.stopPropagation(); };
    disabledBtns.forEach((btn) => btn.addEventListener('click', onDisabledClick));

    // ---- store / window subscriptions ----
    const offDataset = store.subscribe('params.dataset', () => refreshDatasetLabel());
    const offField   = store.subscribe('params.fieldComponent', () => refreshFieldLabel());
    // After a dataset load, field metadata is fresh — rebuild option lists
    // (the open dropdown will pick up the new options on next open via
    // refresh()) and re-resolve labels.
    const offSection = store.onIntent(INTENTS.UI_PANE_REBUILD_SECTION, (p) => {
        if (p && (p as { which?: string }).which === 'data') {
            refreshDatasetLabel();
            refreshFieldLabel();
            datasetDD?.refresh();
            fieldDD?.refresh();
        }
    });
    const onUploadedChanged = (): void => { datasetDD?.refresh(); };
    window.addEventListener(UPLOADED_DATASETS_CHANGED, onUploadedChanged);

    // Timestep scrubber — renders inert until a multi-step dataset arrives.
    const offScrubber = installTimestepScrubber();

    // Fold the scrubber into the expanded tray when the pill can't fit it.
    const offFit = installTopbarFit();

    // Cold start: unfurl the logo+wordmark badge to the full bar on reveal.
    const offReveal = installTopbarReveal();

    // Initial paint.
    refreshDatasetLabel();
    refreshFieldLabel();

    return () => {
        offDataset();
        offField();
        offSection();
        offScrubber();
        offFit();
        offReveal();
        window.removeEventListener(UPLOADED_DATASETS_CHANGED, onUploadedChanged);
        if (helpObserver) helpObserver.disconnect();
        if (consoleObserver) consoleObserver.disconnect();
        if (uploadBtn)  uploadBtn.removeEventListener('click', onUploadClick);
        if (helpBtn)    helpBtn.removeEventListener('click', onHelpClick);
        if (consoleBtn) consoleBtn.removeEventListener('click', onConsoleClick);
        if (expandBtn)  expandBtn.removeEventListener('click', onExpandClick);
        disabledBtns.forEach((btn) => btn.removeEventListener('click', onDisabledClick));
        datasetDD?.dispose();
        fieldDD?.dispose();
    };
}
